import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Form, ListGroup } from 'react-bootstrap';

function AgreementNameField({
  value,
  onChange,
  label = 'Agreement Name',
  required = false,
  maxLength = 60,
  placeholder = 'Enter or select agreement name...',
  className = 'mb-3',
  groupClassName,
  active = true,
  refreshKey,
  inputName,
}) {
  const [names, setNames] = useState([]);
  const [showMatches, setShowMatches] = useState(false);
  const [loading, setLoading] = useState(false);
  const [browseAll, setBrowseAll] = useState(false);

  useEffect(() => {
    if (!active) {
      setShowMatches(false);
      setBrowseAll(false);
      return undefined;
    }

    let cancelled = false;

    const fetchNames = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/api/pricing-agreements/names');
        if (!cancelled) {
          setNames(Array.isArray(response.data) ? response.data : []);
        }
      } catch {
        if (!cancelled) {
          setNames([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchNames();
    return () => {
      cancelled = true;
    };
  }, [active, refreshKey]);

  const filteredNames = useMemo(() => {
    if (browseAll) {
      return names.slice(0, 50);
    }

    const query = (value || '').trim().toLowerCase();
    if (!query) {
      return names.slice(0, 50);
    }

    return names.filter((name) => name.toLowerCase().includes(query)).slice(0, 50);
  }, [names, value, browseAll]);

  const handleSelectName = (name) => {
    onChange(name);
    setBrowseAll(false);
    setShowMatches(false);
  };

  return (
    <Form.Group className={groupClassName || `${className} position-relative`}>
      <Form.Label>
        {label}
        {required ? ' *' : ''}
      </Form.Label>
      <Form.Control
        type="text"
        name={inputName}
        maxLength={maxLength}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          setBrowseAll(false);
          onChange(e.target.value);
        }}
        onFocus={() => {
          setBrowseAll(true);
          setShowMatches(true);
        }}
        onBlur={() => {
          window.setTimeout(() => {
            setShowMatches(false);
            setBrowseAll(false);
          }, 150);
        }}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        aria-required={required || undefined}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      />
      {active && showMatches && loading && (
        <div className="small text-muted mt-1">Loading agreement names...</div>
      )}
      {active && showMatches && !loading && filteredNames.length > 0 && (
        <ListGroup
          className="position-absolute w-100 shadow-sm"
          style={{ zIndex: 2, maxHeight: '220px', overflowY: 'auto' }}
        >
          {filteredNames.map((name) => (
            <ListGroup.Item
              key={name}
              action
              active={name === value}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSelectName(name)}
            >
              {name}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
      {active &&
        showMatches &&
        !loading &&
        !browseAll &&
        (value || '').trim() &&
        filteredNames.length === 0 && (
          <div className="small text-muted mt-1">
            {names.length > 0 ? 'No matching agreement names' : 'No saved agreement names yet'}
          </div>
        )}
    </Form.Group>
  );
}

export default AgreementNameField;
